import {useContext, useState} from 'react';
import classNames from 'classnames';
import {Gallery as PhotoswipeGallery, Item} from 'react-photoswipe-gallery';
import {WebsiteContext} from '../../../websites';
import useCdn from '../../../websites/src/hooks/cdn-hook';
import {Image} from '../../../websites/src/components/image';
import GalleryCarousel from '../../../websites/src/components/gallery-carousel';
import {Caption, getCaption} from './gallery-caption';
import {getGalleryCategoryId, getGalleryImage, getVisibleGalleryItems} from './gallery-items';

const allId = '__gallery_all__';
const galleryOptions = {
    shareEl: false, showHideOpacity: true, maxSpreadZoom: 4, fullscreenEl: false,
    getDoubleTapZoom: (isMouseClick, item) => isMouseClick ? 2 : item.initialZoomLevel < 0.7 ? 1 : 4
};

function GalleryImage({item, label, category, thumbnails, index}) {
    const context = useContext(WebsiteContext);
    const source = getGalleryImage(item);
    const original = useCdn(source, null, 1680);
    const thumbnail = useCdn(source, null, 480);
    const [dimensions, setDimensions] = useState({width: 1680, height: 1680});
    const actionLabel = `Open ${item.title?.trim() || item.altText?.trim() || `${label || 'Gallery'} Image ${index + 1}`}`;
    return <Item original={original} thumbnail={thumbnail} width={dimensions.width} height={dimensions.height}
                 title={getCaption(item, category)}>
        {({ref, open}) => <div className="flex flex-col rounded-theme section-grey overflow-hidden">
            <button type="button" ref={ref} aria-label={actionLabel}
                    className="block w-full text-left hover:opacity-75 focus:outline-none focus:ring-2"
                    onLoadCapture={event => {
                        const image = event.target;
                        if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                            setDimensions({width: 1680, height: Math.round(1680 * image.naturalHeight / image.naturalWidth)});
                        }
                    }}
                    onClick={context?.builder ? undefined : open}>
                <Image alt={item.altText || ''} src={source}
                       pictureClassName="block w-full h-full"
                       className={classNames('w-full h-full object-center aspect-square', thumbnails ? 'object-contain' : 'object-cover')}/>
            </button>
            <Caption item={item}/>
        </div>}
    </Item>;
}

export default function GalleryDisplay(props) {
    const items = getVisibleGalleryItems(props.value);
    const categories = Array.isArray(props.categories) ? props.categories : [];
    const [selectedCategory, setSelectedCategory] = useState(allId);
    const tabs = [{id: allId, title: 'All', items}, ...categories.map(category => ({...category,
        items: items.filter(item => getGalleryCategoryId(item.category) === category.id)
    })).filter(category => category.items.length)];
    const activeTab = tabs.find(tab => tab.id === selectedCategory) || tabs[0];
    const thumbnails = props.design === 'thumbnails';
    if (!items.length) return null;

    return <>
        {props.label && !props.hideLabel ? <h3 className="!text-xl !my-1">{props.label}</h3> : null}
        {tabs.length > 2 ? <nav aria-label={`${props.label || 'Gallery'} Categories`} className="flex gap-2 overflow-x-auto py-2">
            {tabs.map(tab => <button key={tab.id} type="button" aria-pressed={activeTab.id === tab.id}
                onClick={event => {
                    setSelectedCategory(tab.id);
                    event.currentTarget.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
                }} className={classNames('px-2.5 py-2 text-base font-semibold shrink-0 border-b-2',
                    activeTab.id === tab.id ? 'border-current' : 'border-transparent opacity-70')}>
                {tab.title}<span className="ml-1.5 rounded-full px-2 py-0.5 text-xs bg-gray-100 text-gray-900">{tab.items.length}</span>
            </button>)}
        </nav> : null}
        {props.websiteLayout === 'carousel' ? <GalleryCarousel key={activeTab.id} items={activeTab.items}
            categories={categories} label={props.label || 'Gallery'} thumbnails={thumbnails}/> : <PhotoswipeGallery options={galleryOptions}>
            <div className="mb-4 grid grid-cols-2 gap-4 pt-2 pb-4 sm:grid-cols-4 text-sm"
                 aria-label={thumbnails ? `${props.label || 'Gallery'} Images` : undefined}>
                {activeTab.items.map((item, index) => getGalleryImage(item)
                    ? <GalleryImage key={item.id || index} item={item} index={index} label={props.label}
                                    category={categories.find(category => category.id === getGalleryCategoryId(item.category))} thumbnails={thumbnails}/>
                    : <div key={item.id || index} className="rounded-theme section-grey"><Caption item={item} truncate={false}/></div>)}
            </div>
        </PhotoswipeGallery>}
    </>;
}
